import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { X, Play, Info, PlayCircle, Maximize2, MapPin, Heart, Radio } from 'lucide-react';
import { VideoInfo, useWingmanStore } from '../../store/useWingmanStore';
import { spatialCommandController } from '../../spatial/spatialCommandController';
import { accessControl } from '../../services/accessControl';

// Safe Event Bus Dispatcher for Orb
const emitOrbState = (state: string) => {
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('ORB_COMMAND', { detail: { state } }));
    }
};

interface Props {
    video: VideoInfo & { isPremium?: boolean };
    isOpen: boolean;
    onClose: () => void;
    isFeedMode?: boolean;
    inline?: boolean;
}

/**
 * 👑 Cinematic Media Card (Premium Vault)
 * Bypasses YouTube to render high-end cinematic Vimeo/HTML5 video players with no ads or algorithm spam.
 */
export const CinematicMediaCard: React.FC<Props> = ({ video, isOpen, onClose, isFeedMode = false, inline = false }) => {
    if (!video) return null;

    const [isFullscreen, setIsFullscreen] = useState(false);
    const [videoError, setVideoError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [retryKey, setRetryKey] = useState(0);
    
    const containerRef = useRef<HTMLDivElement>(null);
    const controls = useAnimation();
    
    const savedVideos = useWingmanStore(state => state.savedVideos);
    const saveVideo = useWingmanStore(state => state.saveVideo);
    const unsaveVideo = useWingmanStore(state => state.unsaveVideo);
    const userTier = useWingmanStore(state => state.userTier);
    const triggerUpgradePrompt = useWingmanStore(state => state.triggerUpgradePrompt);
    
    const isSaved = savedVideos.some(v => v.url === video.url);

    // Determine if it's a Vimeo link
    const isVimeo = video.url.includes('vimeo.com');
    const vimeoId = isVimeo ? video.url.split('vimeo.com/')[1]?.split('?')[0] : null;

    // 🔥 ORB REACTION
    useEffect(() => {
        if (isOpen && !inline) {
            emitOrbState('MEDIA_ACTIVE');
        } else {
            emitOrbState('IDLE');
        }
    }, [isOpen, inline]);

    // 🔥 CLICK → MAP SYNC
    const handleFlyToMap = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (video.coordinates) {
            spatialCommandController.enqueueCommand({
                type: 'FLY_TO',
                source: 'USER',
                params: {
                    center: video.coordinates,
                    zoom: 17,
                    pitch: 65,
                    bearing: -30,
                    duration: 4000,
                    essential: true
                }
            });
            onClose(); 
        }
    };

    const toggleFullscreen = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!containerRef.current) return;
        
        if (!document.fullscreenElement) {
            containerRef.current.requestFullscreen().catch(err => {
                console.error(`Error attempting to enable full-screen mode: ${err.message}`);
            });
            setIsFullscreen(true);
        } else {
            document.exitFullscreen();
            setIsFullscreen(false);
        }
    };

    const handleDragEnd = (event: any, info: any) => {
        if (info.offset.y > 100 || info.offset.y < -100) {
            onClose();
        } else {
            controls.start({ y: 0 });
        }
    };

    return (
        <AnimatePresence>
            {(isOpen || isFeedMode) && (
                <motion.div
                    initial={isFeedMode ? { opacity: 1 } : { opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className={inline ? "relative w-full" : isFeedMode ? "h-[100dvh] w-full snap-start relative bg-black" : "fixed inset-0 z-[3000] flex items-center justify-center p-4 md:p-8 bg-black/95 backdrop-blur-xl"}
                    onClick={isFeedMode || inline ? undefined : onClose}
                >
                    <motion.div
                        ref={containerRef}
                        initial={isFeedMode || inline ? { scale: 1, y: 0, opacity: 1 } : { scale: 0.9, y: 20, opacity: 0 }}
                        animate={controls}
                        whileInView={isFeedMode || inline ? undefined : { scale: 1, y: 0, opacity: 1 }}
                        exit={isFeedMode || inline ? { opacity: 0 } : { scale: 0.9, y: 100, opacity: 0 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        drag={isFeedMode || inline ? undefined : "y"}
                        dragConstraints={{ top: 0, bottom: 0 }}
                        dragElastic={0.2}
                        onDragEnd={handleDragEnd}
                        className={isFeedMode || inline
                            ? "relative h-full w-full bg-black overflow-hidden" 
                            : `relative w-full ${isFullscreen ? 'h-[100dvh] max-w-none rounded-none border-none' : 'max-w-5xl rounded-3xl border border-[#D4AF37]/20 shadow-[0_0_80px_rgba(212,175,55,0.15)]'} bg-black overflow-hidden touch-none`
                        }
                        onClick={(e) => e.stopPropagation()} 
                    >
                        {/* Premium Header/Badging */}
                        <div className="absolute top-0 left-0 right-0 z-10 p-4 bg-gradient-to-b from-black/80 to-transparent flex items-start justify-between pointer-events-none">
                            <div className="flex flex-col gap-1">
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#D4AF37]/20 border border-[#D4AF37]/30 text-[#D4AF37] text-[10px] font-black uppercase tracking-widest backdrop-blur-md">
                                    <PlayCircle className="w-3 h-3" />
                                    Wingman Elite Exclusives
                                </span>
                                
                                {video.isLive && (
                                    <motion.span 
                                        initial={{ opacity: 0.5 }}
                                        animate={{ opacity: [0.5, 1, 0.5] }}
                                        transition={{ duration: 2, repeat: Infinity }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (!accessControl.canAccess('LIVE_VIBE', userTier as any)) {
                                                triggerUpgradePrompt('LIVE_VIBE');
                                            }
                                        }}
                                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/20 border border-red-500/30 text-red-500 text-[10px] font-black uppercase tracking-widest backdrop-blur-md pointer-events-auto cursor-pointer hover:bg-red-500/30 transition-colors"
                                    >
                                        <Radio className="w-3 h-3" />
                                        Live Now
                                    </motion.span>
                                )}
                                <h2 className="text-white font-medium text-lg lg:text-xl drop-shadow-md max-w-2xl mt-2 line-clamp-2">
                                    {video.title?.replace(/&amp;/g, '&')}
                                </h2>
                            </div>
                            
                            <div className="flex items-center gap-3">
                                {video.coordinates && (
                                    <motion.button
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={handleFlyToMap}
                                        className="pointer-events-auto flex items-center gap-1.5 px-3 py-1.5 bg-black/60 hover:bg-[#D4AF37]/20 border border-white/10 hover:border-[#D4AF37]/50 rounded-full text-white/90 hover:text-[#D4AF37] backdrop-blur-md transition-all duration-100 active:scale-95 text-xs font-bold uppercase tracking-wider"
                                    >
                                        <MapPin className="w-3.5 h-3.5" />
                                        Sync Map
                                    </motion.button>
                                )}

                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (isSaved) {
                                                const identifier = (video as any).id || video.url;
                                                unsaveVideo(identifier);
                                            } else {
                                            if (!accessControl.canAccess('SAVED_VAULT', userTier, { count: savedVideos.length })) {
                                                triggerUpgradePrompt('SAVED_VAULT');
                                            } else {
                                                saveVideo(video);
                                            }
                                        }
                                    }}
                                    className={`pointer-events-auto flex items-center gap-1.5 px-3 py-1.5 ${isSaved ? 'bg-red-500/20 border-red-500/50 text-red-400' : 'bg-black/60 border-white/10 text-white/90'} border rounded-full backdrop-blur-md transition-all duration-100 active:scale-95 text-xs font-bold uppercase tracking-wider`}
                                >
                                    <Heart className={`w-3.5 h-3.5 ${isSaved ? 'fill-red-500' : ''}`} />
                                    {isSaved ? 'Saved' : 'Save'}
                                </motion.button>
                                
                                <button
                                    onClick={toggleFullscreen}
                                    className="pointer-events-auto p-2 bg-black/50 hover:bg-black/80 rounded-full text-white/70 hover:text-white backdrop-blur-md transition-all duration-100 active:scale-95 border border-white/10"
                                >
                                    <Maximize2 className="w-5 h-5" />
                                </button>
                                
                                <button
                                    onClick={onClose}
                                    className={`${isFeedMode ? 'hidden' : ''} pointer-events-auto p-2 bg-black/50 hover:bg-black/80 rounded-full text-white/70 hover:text-white backdrop-blur-md transition-all duration-100 active:scale-95 border border-white/10`}
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        {/* Player Container */}
                        {/* Player Container */}
                         <div className={`relative w-full bg-black ${isFullscreen ? 'h-[100dvh]' : 'aspect-video'}`}>
                            {videoError ? (
                                <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
                                    <img 
                                        src={video.thumbnail} 
                                        className="w-full h-full object-cover blur-md opacity-40 scale-110" 
                                        alt="" 
                                    />
                                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-6 p-8 text-center bg-black/60">
                                        <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center border border-white/10 mb-2">
                                            <Radio className="w-10 h-10 text-red-500/50" />
                                        </div>
                                        <div>
                                            <h3 className="text-white font-bold text-lg mb-1 uppercase tracking-wider">Playback Encumbered</h3>
                                            <p className="text-white/60 text-sm max-w-xs">The spatial signal from YouTube was interrupted or blocked.</p>
                                        </div>
                                        <button 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setVideoError(false);
                                                setIsLoading(true);
                                                setRetryKey(prev => prev + 1);
                                            }}
                                            className="px-8 py-3 bg-[#D4AF37] text-black rounded-full font-black text-xs uppercase tracking-widest hover:scale-105 active:scale-95 transition-all shadow-[0_0_30px_rgba(212,175,55,0.4)]"
                                        >
                                            Retry Intelligence Link
                                        </button>
                                    </div>
                                </div>
                            ) : (isOpen && isVimeo && vimeoId) ? (
                                <iframe
                                    key={`vimeo-${retryKey}`}
                                    src={`https://player.vimeo.com/video/${vimeoId}?autoplay=1&muted=0&title=0&byline=0&portrait=0&background=0`}
                                    className="absolute inset-0 w-full h-full"
                                    frameBorder="0"
                                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                                    allowFullScreen
                                />
                            ) : (isOpen && video.url.includes('youtube.com')) ? (
                                <iframe
                                    key={`yt-${retryKey}`}
                                    src={`https://www.youtube.com/embed/${video.url.split('v=')[1]?.split('&')[0]}?autoplay=1&mute=0&controls=1&modestbranding=1&rel=0`}
                                    className="absolute inset-0 w-full h-full"
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    onLoad={() => setIsLoading(false)}
                                />
                            ) : (isOpen && video.url.includes('youtu.be')) ? (
                                <iframe
                                    key={`yt-short-${retryKey}`}
                                    src={`https://www.youtube.com/embed/${video.url.split('youtu.be/')[1]?.split('?')[0]}?autoplay=1&mute=0&controls=1&modestbranding=1&rel=0`}
                                    className="absolute inset-0 w-full h-full"
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    onLoad={() => setIsLoading(false)}
                                />
                            ) : (
                                <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
                                    <img 
                                        src={video.thumbnail} 
                                        className="w-full h-full object-cover blur-sm opacity-50" 
                                        alt="" 
                                    />
                                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
                                        <Play className="w-12 h-12 text-[#D4AF37]/50" />
                                        <p className="text-white/50 text-sm">Media Source Pending...</p>
                                    </div>
                                </div>
                            )}

                            {/* Loading State Overlay */}
                            {isLoading && !videoError && isOpen && (
                                <div className="absolute inset-0 z-20 flex items-center justify-center bg-black">
                                    <img 
                                        src={video.thumbnail} 
                                        className="absolute inset-0 w-full h-full object-cover blur-2xl opacity-30 scale-125" 
                                        alt="" 
                                    />
                                    <div className="relative flex flex-col items-center gap-6">
                                        <div className="w-12 h-12 border-2 border-[#D4AF37]/20 border-t-[#D4AF37] rounded-full animate-spin" />
                                        <div className="flex flex-col items-center gap-1">
                                            <span className="text-[#D4AF37] text-[10px] font-black uppercase tracking-[0.3em] animate-pulse">Establishing Link</span>
                                            <span className="text-white/30 text-[9px] font-medium uppercase tracking-widest">Spatial Intelligence Relay</span>
                                        </div>
                                    </div>
                                    
                                    {/* Manual Error Trigger (Safety Valve) */}
                                    <button 
                                        onClick={() => setVideoError(true)}
                                        className="absolute bottom-10 px-4 py-2 text-white/20 hover:text-white/50 text-[10px] uppercase tracking-widest transition-colors"
                                    >
                                        Taking too long? Tap for fallback.
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Premium Footer */}
                        <div className="p-6 bg-gradient-to-t from-black via-black/90 to-transparent absolute bottom-0 left-0 right-0 pointer-events-none">
                            <div className="flex flex-col gap-2 max-w-3xl pointer-events-auto">
                                <div className="flex items-center gap-2">
                                    <span className="px-2 py-0.5 rounded-sm bg-[#D4AF37] text-black text-[9px] font-black uppercase tracking-tighter">AI INSIGHT</span>
                                    <span className="text-[#D4AF37]/60 text-[10px] font-medium uppercase tracking-widest">Intelligence Layer</span>
                                </div>
                                <p className="text-white font-medium text-sm leading-relaxed drop-shadow-lg">
                                    {video.summary || video.description || "Professional spatial intelligence layer for elite location exploration."}
                                </p>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
